import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gsp-text-primary hover:text-gsp-teal transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gsp-text-primary mb-2">
            Terms of Service
          </h1>
          <p className="text-gsp-text-body mb-8">Version: January 2026</p>

          <div className="space-y-8 text-gsp-text-body">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                1. Acceptance of Terms
              </h2>
              <p className="leading-relaxed">
                By accessing or using our services, you agree to be bound by these Terms of Service ("Terms").
                If you do not agree to all of the terms and conditions, you may not access or use the services.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                2. Description of Service
              </h2>
              <p className="leading-relaxed">
                AXCS Capital, Inc., directly or through one or more of its affiliated entities (AXCS Capital, Inc.
                and all such affiliated entities together "AXCS"), provides an AI-powered document analysis platform
                for commercial real estate (the "Services"). The Services include, but may not be limited to,
                document processing, data extraction, analysis, and related functionalities delivered through our
                proprietary technology platform.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                3. Use of Services
              </h2>
              <div className="space-y-4">
                <p className="leading-relaxed">
                  You may not use the Services for any illegal, harmful, or wrongful activity. You agree to comply
                  with all applicable laws and regulations, and any other legal or contractual obligations to which
                  you or any information you provide are subject, in your use of the Services. You may not use the
                  Services in a way that violates or infringes upon any party's legal or contractual rights. You must
                  stay within any usage limits or rate limits imposed by the Services. We reserve the right to suspend
                  or permanently ban users who attempt to circumvent usage limits or abuse the Services in any way.
                </p>
                <p className="leading-relaxed">You agree not to:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code or underlying structure of the platform;</li>
                  <li>Modify, translate, or create derivative works based on the platform or Services;</li>
                  <li>Resell, sublicense, or provide access to the Services to any third party;</li>
                  <li>Remove any proprietary notices or labels from the platform; or</li>
                  <li>Use the Services in any manner that could damage, disable, or impair the platform.</li>
                </ul>
              </div>
            </section>

            {/* Section 4 - Key Section */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                4. Your Data and Our Rights
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gsp-text-primary mb-2">
                    4.1 User Responsibility
                  </h3>
                  <p className="leading-relaxed">
                    You are solely responsible for the data you upload to the Services. Do not upload
                    confidential, proprietary, or sensitive information that you do not have the right
                    to share or that you would not want used as described below. By using the Services,
                    you represent and warrant that you have all rights, permissions, licenses, or approvals
                    necessary to upload the information you provide to obtain the Services.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gsp-text-primary mb-2">
                    4.2 No Personal Information
                  </h3>
                  <p className="leading-relaxed">
                    Do not upload documents containing personally identifiable information ("PII") such as
                    Social Security numbers, personal financial information, personal health information,
                    or other private personal data. The Services are designed for commercial real estate
                    documents and are not intended to process personal information.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gsp-text-primary mb-2">
                    4.3 License Grant
                  </h3>
                  <p className="leading-relaxed">
                    By uploading data to the Services, you grant AXCS a perpetual, irrevocable,
                    worldwide, royalty-free license to use, reproduce, modify, analyze, store, share,
                    and incorporate such data for any purpose, including but not limited to AI model
                    training, machine learning, service improvement, product development, analytics,
                    and any other business or commercial purpose.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gsp-text-primary mb-2">
                    4.4 Data Handling
                  </h3>
                  <p className="leading-relaxed">
                    While we implement reasonable security measures to protect data in our systems, we make
                    no guarantees regarding confidentiality. You acknowledge that uploaded data may be processed
                    by one or more third-party providers (each a "Third-Party Provider"), which may include AI
                    services and infrastructure providers, and portions of the output provided by the Services
                    may include output from one or more Third Party Providers. Any services provided by a
                    Third-Party Provider, and any output from such Third-Party Provider services, are subject
                    to the separate terms and conditions of each such Third-Party Provider, and AXCS is not
                    responsible for such terms and conditions or any such Third-Party Provider outputs. You
                    acknowledge that uploaded data may be transmitted or stored on cloud-based remote servers.
                    You should not upload any information that requires strict confidentiality protections.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                5. Intellectual Property
              </h2>
              <div className="space-y-4">
                <p className="leading-relaxed">
                  AXCS owns all rights, title, and interest in and to the platform, including
                  all software, technology, documentation, and related intellectual property.
                </p>
                <p className="leading-relaxed">
                  You retain ownership of your original data, subject to the license grant in Section 4.3 above.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                6. Disclaimers and Limitations
              </h2>
              <div className="space-y-4">
                <p className="leading-relaxed font-semibold">
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                  EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
                  FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p className="leading-relaxed">
                  The Services utilize large language models (LLMs) and artificial intelligence technologies
                  that may generate inaccurate, incomplete, or erroneous outputs. You acknowledge and agree
                  that all outputs from the Services require human review and verification before reliance
                  or use in any business decision or transaction. You assume full responsibility for validating
                  the accuracy and completeness of all Service outputs and your use of the Service outputs is
                  at your sole risk.
                </p>
                <p className="leading-relaxed font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, NONE OF AXCS, NOR ANY OF ITS OFFICERS, DIRECTORS,
                  MEMBERS, PARTNERS, EMPLOYEES, OR AGENTS (EACH AN "AXCS PARTY" AND COLLECTIVELY, THE "AXCS
                  PARTIES") SHALL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                  DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY
                  LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICES.
                  IN THE EVENT YOU MAKE ANY CLAIM AGAINST ANY AXCS PARTY IN CONNECTION WITH YOUR USE OF THE
                  SERVICES OR THE OUTPUT PROVIDED BY THE SERVICES, THE AGGREGATE LIABILITY OF ALL AXCS PARTIES
                  WILL NOT EXCEED THE GREATER OF (I) THE AGGREGATE AMOUNT (IF ANY) THAT YOU PAID TO AXCS FOR
                  ACCESS TO OR USE OF THE SERVICES IN THE TWELVE (12) MONTHS PRIOR TO THE DATE OF THE CLAIM
                  MADE, AND (II) ONE HUNDRED DOLLARS ($100). THE LIMITATIONS IN THIS SECTION APPLY TO THE
                  MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW. THE LIMITATIONS SET FORTH HEREIN ARE A MATERIAL
                  CONDITION TO PROVISION OF THE SERVICES BY AXCS AND YOU ACKNOWLEDGE THAT AXCS WOULD NOT PROVIDE,
                  AND YOU WOULD NOT HAVE ACCESS TO, THE SERVICES, BUT FOR YOUR ACCEPTANCE OF THESE LIMITATIONS.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                7. Indemnification
              </h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless each AXCS Party for, from and against any
                claims, liabilities, damages, losses, and expenses arising out of or in any way connected
                with your access to or use of the Services, your violation of these Terms, or your violation
                of any third-party rights.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-semibold text-gsp-text-primary mb-3">
                8. General
              </h2>
              <ul className="space-y-4">
                <li className="leading-relaxed">
                  <span className="font-semibold">Governing Law:</span> These Terms shall be governed by and
                  construed in accordance with the laws of the State of California, without regard to its
                  conflict of laws principles.
                </li>
                <li className="leading-relaxed">
                  <span className="font-semibold">Dispute Resolution - MANDATORY ARBITRATION:</span> You
                  acknowledge and agree that, except as expressly set forth below, any claims arising out
                  of or relating to these Terms or the Services shall be resolved only through final and
                  binding arbitration and judgment upon the award rendered by the arbitrator may be entered
                  in any court having jurisdiction thereof. The arbitration shall be based out of Los Angeles
                  County, California and the parties hereby consent to the jurisdiction of the arbitrator
                  and to service of process. BY USING THE SERVICES, YOU ARE AGREEING TO HAVE ANY CLAIM
                  HEREUNDER DECIDED BY NEUTRAL ARBITRATION AND YOU ARE GIVING UP THE RIGHT TO A JURY OR
                  COURT TRIAL. If you do not agree to this mandatory arbitration provision, you should not
                  use the Services. Notwithstanding the foregoing, AXCS shall not be required to arbitrate
                  or seek through arbitration any injunctive or other equitable relief to stop or prevent
                  your unauthorized use or abuse of the Services, or your misappropriation of, or infringement
                  upon, the intellectual property rights of AXCS or any third party.
                </li>
                <li className="leading-relaxed">
                  <span className="font-semibold">Modifications:</span> We reserve the right to modify these
                  Terms at any time. Your continued use of the Services following any changes constitutes
                  your acceptance of the revised Terms.
                </li>
                <li className="leading-relaxed">
                  <span className="font-semibold">Assignment:</span> You may not assign or transfer these
                  Terms without our prior written consent.
                </li>
                <li className="leading-relaxed">
                  <span className="font-semibold">Publicity:</span> We may reference you as a user of the
                  Services in communications with investors or other third parties, and we may include your
                  name and logo in customer lists and marketing materials.
                </li>
                <li className="leading-relaxed">
                  <span className="font-semibold">Entire Agreement:</span> These Terms constitute the entire
                  agreement between you and AXCS regarding the Services.
                </li>
                <li className="leading-relaxed">
                  <span className="font-semibold">Severability:</span> If any part of these Terms is found
                  to be illegal or unenforceable, the remainder of the Terms will remain in effect to the
                  greatest extent permitted by applicable law.
                </li>
              </ul>
            </section>

            {/* Footer */}
            <footer className="pt-8 mt-8 border-t border-gray-200">
              <p className="text-sm text-gsp-text-body">
                &copy; 2026 AXCS Capital, Inc. All rights reserved.
              </p>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
